import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { AdminSkeleton } from './admin-skeleton';
import { EventUtilizationCard } from './event-utilization';
import { EventsByOrganizerCard } from './events-by-organizer';
import {
  useEventUtilization,
  useLicenseConsumption,
} from './hooks/use-admin-kpis';
import { useEventsByOrganizer } from './hooks/use-events-by-organizer';
import { LicenseConsumptionCard } from './license-consumption';

export const Admin = () => {
  const { t } = useTranslation();
  const {
    data: licenseConsumption,
    isLoading: isLicenseLoading,
    isError: isLicenseError,
  } = useLicenseConsumption();
  const {
    data: eventUtilization,
    isLoading: isEventUtilLoading,
    isError: isEventUtilError,
  } = useEventUtilization();
  const {
    data: organizerData,
    isLoading: isOrganizerLoading,
    isError: isOrganizerError,
    isRefetchError: isOrganizerRefetchError,
    hasNextPage,
    isFetchingNextPage,
    fetchMore,
    sortKey: organizerSortKey,
    reverse: organizerReverse,
    onSort: handleOrganizerSort,
  } = useEventsByOrganizer();

  useEffect(() => {
    if (!isOrganizerRefetchError) {
      return;
    }

    toast.error('analytics.admin.loading-more-error', {
      id: 'analytics-admin-loading-more-error',
    });
  }, [isOrganizerRefetchError]);

  return (
    <div
      className="flex flex-col gap-6"
      aria-busy={isLicenseLoading || isEventUtilLoading || isOrganizerLoading}
      aria-label={
        isLicenseLoading || isEventUtilLoading || isOrganizerLoading
          ? t('analytics.admin.loading')
          : undefined
      }
    >
      <h2 className="sr-only">{t('analytics.admin.title')}</h2>
      <div className="flex gap-6">
        <div className="flex flex-1">
          {isLicenseLoading ? (
            <AdminSkeleton variant="kpiTiles" />
          ) : (
            <LicenseConsumptionCard
              data={licenseConsumption ?? null}
              isError={isLicenseError}
            />
          )}
        </div>
        <div className="flex flex-1">
          {isEventUtilLoading ? (
            <AdminSkeleton variant="kpiTiles" />
          ) : (
            <EventUtilizationCard
              data={eventUtilization ?? null}
              isError={isEventUtilError}
            />
          )}
        </div>
      </div>
      {isOrganizerLoading ? (
        <AdminSkeleton variant="card" />
      ) : (
        <EventsByOrganizerCard
          data={organizerData}
          title={t('analytics.admin.top-event-creators')}
          tooltipContent={t('analytics.admin.top-event-creators-tooltip')}
          columnHeaders={{
            name: t('analytics.admin.organizer-column'),
            email: t('analytics.admin.organizer-email-column'),
            status: t('analytics.admin.status-column'),
            seatAllocationDate: t('analytics.admin.seat-allocation-date'),
            seatAllocationDateTooltip: t(
              'analytics.admin.seat-allocation-date-tooltip',
            ),
            recentEvent: t('analytics.admin.recent-event'),
            recentEventTooltip: t('analytics.admin.recent-event-tooltip'),
            eventsHosted: t('analytics.admin.events-hosted'),
            eventsCreated: t('analytics.admin.events-created-count'),
            eventBreakdown: t('analytics.admin.event-breakdown'),
          }}
          statusLabels={{
            active: t('analytics.admin.status-active'),
            inviteSent: t('analytics.admin.status-invite-sent'),
            deleted: t('analytics.admin.status-deleted'),
            rejected: t('analytics.admin.status-rejected'),
          }}
          eventTypeLabels={{
            webinar: t('analytics.admin.event-type-webinar'),
            virtual: t('analytics.admin.event-type-virtual'),
            hybrid: t('analytics.admin.event-type-hybrid'),
            onSite: t('analytics.admin.event-type-on-site'),
          }}
          isError={isOrganizerError}
          inviteYourTeamLabel={t('analytics.admin.invite-your-team')}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchMore={fetchMore}
          sortKey={organizerSortKey}
          reverse={organizerReverse}
          onSort={handleOrganizerSort}
        />
      )}
    </div>
  );
};
